import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import ISO6391 from 'iso-639-1';
import { Grid, Modal, Typography, TextField, Autocomplete, Chip, Stack, Select, MenuItem } from "@mui/material";

import { Button, Alert } from "../../../common/styled";
import ModalBox from "../../../../common/ModalBox";

import useLLMConfig from "../../../../../hooks/user/useLLMConfig";

const MatchTypes = ['sentence', 'full'];

function LanguageModal({ open, handleClose, zoneUid }) {
  const { llmPromptScanners, configLLMSettingPromptScanners } = useLLMConfig();

  const allLangCodes = ISO6391.getAllCodes();

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        valid_languages: llmPromptScanners ? llmPromptScanners?.language?.valid_languages : [],
        match_type: llmPromptScanners ? llmPromptScanners?.language?.match_type : "",
        threshold: llmPromptScanners ? llmPromptScanners?.language?.threshold : 0,
      }}
      validationSchema={Yup.object().shape({
        valid_languages: Yup.array().of(Yup.string()),
        match_type: Yup.string().nullable().required("Match type is required"),
        threshold: Yup.number().min(0, "Threshold must be at least 0").max(1, "Threshold must be at most 1"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          values.submit = undefined;
          await configLLMSettingPromptScanners(zoneUid, "language", values);
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, values }) => (
        <React.Fragment>
          <Modal
            open={open}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
          >
            <ModalBox sx={{ width: "60vw" }} title="Edit Language Scanner" handleClose={handleClose}>
              <form noValidate onSubmit={handleSubmit}>
                <Grid container p={4} spacing={3}>
                  <Grid item xs={12}>
                    {errors.submit && (
                      <Alert mt={2} mb={3} variant="outlined" severity="error">
                        {errors.submit}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Threshold</Typography>
                      <TextField
                        fullWidth
                        name="threshold"
                        type="number"
                        error={Boolean(touched.threshold && errors.threshold)}
                        helperText={touched.threshold && errors.threshold}
                        value={values.threshold}
                        onBlur={(e) => {
                          if (e.target.value === "") setFieldValue("threshold", 0);
                        }}
                        onChange={handleChange}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Match Type</Typography>
                      <Select
                        fullWidth
                        name="match_type"
                        value={values.match_type}
                        error={Boolean(touched.match_type && errors.match_type)}
                        helperText={touched.match_type && errors.match_type}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      >
                        {MatchTypes.map((type) => (
                          <MenuItem value={type} key={`match_type_${type}`}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Valid Languages</Typography>
                      <Autocomplete
                        fullWidth
                        multiple
                        options={allLangCodes}
                        value={values.valid_languages}
                        error={Boolean(touched.valid_languages && errors.valid_languages)}
                        helperText={touched.valid_languages && errors.valid_languages}
                        getOptionLabel={(option) => ISO6391.getName(option)}
                        onChange={(event, newValue) => setFieldValue("valid_languages", newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => <Chip variant="filled" color="warning" label={ISO6391.getName(option)} {...getTagProps({ index })} />)
                        }
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} textAlign={"right"} pt={4}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                      <Button variant="outlined" color="secondary" size="modal" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="success" size="modal" disabled={isSubmitting}>
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </ModalBox>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  );
}

export default LanguageModal;
